import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Input,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  RadioGroup, RadioGroupItem,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ContractFormValues } from '../../contract-form';

/** Registration period options — matches legacy PRIVATE_DOMAIN_BILLING_CYCLE. */
const REGISTRATION_PERIOD_OPTIONS = [
  { value: '1year', label: '1 năm' },
  { value: '2years', label: '2 năm' },
  { value: '3years', label: '3 năm' },
  { value: '5years', label: '5 năm' },
  { value: '10years', label: '10 năm' },
];

/** Addons available for VNNIC domain registrations. */
const ADDON_VALUES = ['domain_dns_management', 'domain_email_forwarding', 'domain_id_protection'] as const;
type AddonValue = typeof ADDON_VALUES[number];
const ADDON_LABELS: Record<AddonValue, string> = {
  domain_dns_management: 'DNS Management',
  domain_email_forwarding: 'Email Forwarding',
  domain_id_protection: 'ID Protection',
};

interface DomainMetadataFieldsProps {
  /** `products.{index}.service_domain_metadata` absolute path prefix. */
  metaPath: string;
  /** Show the VNNIC-only fields: addons checkboxes + dns_option radio. */
  isVNNIC?: boolean;
}

/**
 * Domain metadata fields shared by TLDs and VNNIC templates. Renders
 * domain_type (register/transfer), domain_value, domain_registrationperiod,
 * domain_eppcode (transfer only), and optionally VNNIC-specific addons +
 * dns_option. Writes into `service_domain_metadata.*` via RHF `as never` cast
 * (established pattern for paths outside ContractFormValues type's literal set).
 */
export function DomainMetadataFields({ metaPath, isVNNIC = false }: DomainMetadataFieldsProps) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();

  const domainType = (useWatch({ control, name: `${metaPath}.domain_type` as never }) as string) ?? 'register';
  const addons = (useWatch({ control, name: `${metaPath}.addons` as never }) as string[]) ?? [];

  // Ensure domain_type is seeded — avoids blank value on first render.
  useEffect(() => {
    const current = getValues(`${metaPath}.domain_type` as never);
    if (current) return;
    setValue(`${metaPath}.domain_type` as never, 'register' as never, { shouldDirty: true });
  }, [metaPath, getValues, setValue]);

  const toggleAddon = (addonValue: AddonValue, checked: boolean) => {
    const next = checked
      ? [...addons, addonValue]
      : addons.filter((a) => a !== addonValue);
    setValue(`${metaPath}.addons` as never, next as never, { shouldDirty: true });
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Domain type — new registration vs provider transfer */}
      <Field label="Loại đăng ký">
        {() => (
          <RadioGroup
            value={domainType}
            onValueChange={(v) => setValue(`${metaPath}.domain_type` as never, v as never, { shouldDirty: true })}
            style={{ display: 'flex', gap: 'var(--vn-space-4)' }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
              <RadioGroupItem value="register" />
              Đăng ký mới
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
              <RadioGroupItem value="transfer" />
              Đổi nhà cung cấp
            </label>
          </RadioGroup>
        )}
      </Field>

      {/* Domain name */}
      <Controller
        control={control}
        name={`${metaPath}.domain_value` as never}
        render={({ field }) => (
          <Field label="Tên miền">
            {(p) => (
              <Input
                {...p}
                type="text"
                placeholder="example.com"
                value={(field.value as string) ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            )}
          </Field>
        )}
      />

      {/* Registration period */}
      <Field label="Thời hạn đăng ký">
        {(p) => (
          <Select
            value={(useWatch({ control, name: `${metaPath}.domain_registrationperiod` as never }) as string) ?? ''}
            onValueChange={(v) => setValue(`${metaPath}.domain_registrationperiod` as never, v as never, { shouldDirty: true })}
          >
            <SelectTrigger {...p}>
              <SelectValue placeholder="Chọn thời hạn" />
            </SelectTrigger>
            <SelectContent>
              {REGISTRATION_PERIOD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      {/* EPP auth code — only shown for transfer */}
      {domainType === 'transfer' && (
        <Controller
          control={control}
          name={`${metaPath}.domain_eppcode` as never}
          render={({ field }) => (
            <Field label="Mã EPP (Auth Code)">
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  placeholder="EPP auth code"
                  value={(field.value as string) ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              )}
            </Field>
          )}
        />
      )}

      {/* VNNIC-only: addon services */}
      {isVNNIC && (
        <Field label="Dịch vụ bổ sung (Add-ons)">
          {() => (
            <div style={{ display: 'flex', gap: 'var(--vn-space-4)', flexWrap: 'wrap' }}>
              {ADDON_VALUES.map((addonValue) => (
                <label
                  key={addonValue}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}
                >
                  <Checkbox
                    checked={addons.includes(addonValue)}
                    onCheckedChange={(checked) => toggleAddon(addonValue, checked === true)}
                  />
                  {ADDON_LABELS[addonValue]}
                </label>
              ))}
            </div>
          )}
        </Field>
      )}

      {/* VNNIC-only: DNS option */}
      {isVNNIC && (
        <Field label="Tùy chọn DNS">
          {() => (
            <RadioGroup
              value={(useWatch({ control, name: `${metaPath}.dns_option` as never }) as string) ?? 'default'}
              onValueChange={(v) => setValue(`${metaPath}.dns_option` as never, v as never, { shouldDirty: true })}
              style={{ display: 'flex', gap: 'var(--vn-space-4)' }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
                <RadioGroupItem value="default" />
                DNS mặc định
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
                <RadioGroupItem value="custom" />
                DNS khác
              </label>
            </RadioGroup>
          )}
        </Field>
      )}
    </div>
  );
}
